import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AllowedLanguages } from 'i18n';

import { InvoiceDiscrepancy } from 'services/invoiceDiscrepancies/types';
import { logError } from 'services/logging/logging';
import { NotificationType, useNotificationStore } from 'stores/notifications/notifications';
import { InvoiceVerificationDTO, MessageV2DTO } from 'support/types';
import { BasePopup } from 'components/Display/BasePopup/BasePopup';
import { Button } from 'components/Form/Button/Button';

import { generateEmailBody } from '../utils';
import { EmailLanguageToggle } from './EmailLanguageToggle';

const EMAIL_NAMESPACE = 'invoiceVerification';

export const EmailModal = ({
  open,
  onClose,
  invoice,
  invoiceVerification,
  discrepancies,
  language,
  onLanguageChange,
}: {
  open: boolean;
  onClose: () => void;
  invoice: MessageV2DTO;
  invoiceVerification: InvoiceVerificationDTO;
  discrepancies: Array<InvoiceDiscrepancy>;
  language: AllowedLanguages;
  onLanguageChange: (language: AllowedLanguages) => void;
}) => {
  const { t, i18n } = useTranslation();
  const { addNotification } = useNotificationStore();

  // Both catalogues are preloaded in `src/i18n.ts`, so the email can be written in a language the
  // Portal itself is not rendered in. This component stays mounted while the modal is closed, so
  // generation is gated on `open`: a malformed line item must not take down the details page before
  // anyone has asked for the email.
  const emailBody = useMemo(
    () =>
      open
        ? generateEmailBody({
            invoice,
            invoiceVerification,
            discrepancies,
            t: i18n.getFixedT(language, EMAIL_NAMESPACE),
          })
        : '',
    [open, invoice, invoiceVerification, discrepancies, i18n, language],
  );

  const onCopyFailed = (error: unknown) => {
    logError(error);
    addNotification(
      t('invoiceVerification:detailsPage.generateDiscrepancyEmail.copyEmail.error'),
      NotificationType.error,
    );
  };

  const onCopy = () => {
    try {
      // `navigator.clipboard` and `ClipboardItem` are absent outside secure contexts, so this throws
      // synchronously rather than rejecting.
      navigator.clipboard
        .write([
          new ClipboardItem({
            'text/html': new Blob([emailBody], { type: 'text/html' }),
            'text/plain': new Blob([emailBody.replace(/<[^>]*>/g, '')], { type: 'text/plain' }),
          }),
        ])
        .then(() => {
          onClose();
          addNotification(
            t('invoiceVerification:detailsPage.generateDiscrepancyEmail.copyEmail.success'),
            NotificationType.success,
          );
        }, onCopyFailed);
    } catch (error) {
      onCopyFailed(error);
    }
  };

  return (
    <BasePopup open={open} setOpen={onClose} width="xl">
      <BasePopup.Body>
        <div className="flex items-center justify-between gap-4">
          <h1 className="text-lg font-semibold text-gray-900">
            {t('invoiceVerification:detailsPage.generateDiscrepancyEmail.copyEmailModal.title')}
          </h1>
          <EmailLanguageToggle value={language} onChange={onLanguageChange} />
        </div>

        <div
          className="mt-2 max-h-96 overflow-auto rounded-md bg-gray-50 p-4 text-sm text-gray-700"
          dangerouslySetInnerHTML={{ __html: emailBody }}
        ></div>

        <div className="mt-4 flex items-center justify-end gap-2">
          <Button
            variant="secondary"
            onClick={onClose}
            analyticsId="invoiceVerification:generateDiscrepancyEmail.close"
          >
            {t('common:cancel')}
          </Button>
          <Button variant="primary" onClick={onCopy} analyticsId="invoiceVerification:generateDiscrepancyEmail.copy">
            {t('invoiceVerification:detailsPage.generateDiscrepancyEmail.copyEmailModal.copy')}
          </Button>
        </div>
      </BasePopup.Body>
    </BasePopup>
  );
};
